import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight, Settings, Zap } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { NavMain } from "./NavMain";
import { NavUser } from "./NavUser";
import { TeamSwitcher } from "./TeamSwitcher";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
} from "@/shared/components/ui/sidebar";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import logoFull from "@/shared/assets/logo/logo-full.svg";
import { POST_LOSS_ROUTES, UPGRADE_SEARCH_PARAM } from "@/shared/constants/paths";
import { NAV_SECTIONS } from "@/shared/constants/navSections";
import { SupportAvatars } from "@/shared/components/SupportAvatars";
import { usePerson } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useIntercom } from "@/shared/providers/IntercomProvider";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { useIntakeStatus } from "@/shared/hooks/useIntakeStatus";

import { hasMinTier } from "@/shared/utils/tierOrder";
import PostLossPaywall from "@/pages/postloss/components/PostLossPaywall";
import type { PromptLocation } from "@/shared/utils/funnelAnalytics";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { openSidePanel } = useIntercom();
  const navigate = useNavigate();
  const person = usePerson();
  const { assets, debts } = useEstateItems(person?.estate_id);
  const {
    tier,
    isLoading: isTierLoading,
  } = usePaymentTier(person?.estate_id);
  const isPlatinum = hasMinTier(tier, "platinum");
  const intakeStatus = useIntakeStatus();
  const hasCompletedIntake = intakeStatus.hasCompletedIntro;
  const hasCompletedAuthority = intakeStatus.hasCompletedLegalAuthority;
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallLocation, setPaywallLocation] = useState<PromptLocation>("sidebar");
  const [searchParams, setSearchParams] = useSearchParams();
  const hasUpgradeParam = searchParams.has(UPGRADE_SEARCH_PARAM);

  useEffect(() => {
    if (hasUpgradeParam && !isTierLoading) {
      if (!isPlatinum) {
        setPaywallLocation("upgrade_link");
        setPaywallOpen(true);
      }
      setSearchParams(
        (params) => {
          params.delete(UPGRADE_SEARCH_PARAM);
          return params;
        },
        { replace: true },
      );
    }
  }, [hasUpgradeParam, isTierLoading, isPlatinum, setSearchParams]);

  const openPaywall = useCallback(() => {
    setPaywallLocation("sidebar");
    setPaywallOpen(true);
  }, []);

  const pendingCount = useMemo(() => {
    const allItems = [...assets, ...debts];
    return allItems.filter(
      (item) => item.extracted_from_document && item.extraction_status === "pending",
    ).length;
  }, [assets, debts]);

  const sections = useMemo(() => {
    return NAV_SECTIONS.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        if (item.title === "Get authorized") {
          if (!hasCompletedIntake || !hasCompletedAuthority) {
            return { ...item, comingSoon: true };
          }
          return item;
        }
        if (pendingCount && item.url === POST_LOSS_ROUTES.ESTATE_INVENTORY) {
          return { ...item, badge: pendingCount };
        }
        return item;
      }),
    }));
  }, [
    pendingCount,
    hasCompletedIntake,
    hasCompletedAuthority,
  ]);

  return (
    <>
      <Sidebar className="border-r border-primary" {...props}>
        <SidebarHeader className="px-4 pt-4 pb-4 gap-6">
          <Link to={POST_LOSS_ROUTES.OVERVIEW} className="hidden sm:block">
            <img src={logoFull} alt="Elayne" className="h-[28px] self-start" />
          </Link>
          <TeamSwitcher />
        </SidebarHeader>
        <SidebarContent className="px-1">
          <NavMain sections={sections} />
        </SidebarContent>
        <SidebarFooter className="px-3 pb-4 gap-2">
          {!isPlatinum && (
            <button
              onClick={openPaywall}
              className="flex items-center gap-2 rounded-sm border border-blue-300 bg-blue-50 p-3 text-left transition-colors hover:bg-blue-100"
            >
              <div className="flex flex-1 flex-col">
                <span className="text-sm font-medium text-primary">Get more off your plate</span>
                <span className="text-xs text-secondary">See add-ons and pricing</span>
              </div>
              <span
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-blue-200"
                aria-hidden="true"
              >
                <Zap className="h-4 w-4 text-blue-600" />
              </span>
            </button>
          )}
          <Button
            variant="outline"
            onClick={openSidePanel}
            className="lg:hidden flex flex-col items-start gap-1 w-full rounded-lg border border-primary p-3 hover:bg-subtle transition-colors text-left h-auto"
          >
            <div className="flex items-center gap-2 w-full">
              <SupportAvatars size="sm" showOnline />
              <p className="text-sm font-medium flex-1">Get support</p>
              <ChevronRight size={16} className="shrink-0 text-secondary" />
            </div>
            <p className="text-sm text-secondary font-normal">Chat with an Elayne Estate Expert</p>
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate(POST_LOSS_ROUTES.SETTINGS)}
            className="lg:hidden flex items-center gap-3 justify-start px-3 py-2 rounded-md h-auto w-full"
          >
            <Settings size={16} className="shrink-0 text-primary" />
            <p className="text-sm font-normal">Settings</p>
          </Button>
          <NavUser />
        </SidebarFooter>
      </Sidebar>

      <PostLossPaywall
        isOpen={paywallOpen}
        onOpenChange={setPaywallOpen}
        promptLocation={paywallLocation}
        currentTier={tier}
        returnPath={POST_LOSS_ROUTES.OVERVIEW}
      />
    </>
  );
}
